import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { Row, Col, Card, CardHeader, CardBody, Table, Button, Label, Input, Progress } from 'reactstrap';
import { fetchApi, saveApi } from '../../utils/DatabaseHelper';
import { ZUORDNUNG } from '../../utils/TableConstanten';

// Meta-Zähler je Spalte — identisch in Schritt 3 (Preview) und Import-Ergebnis
function MetaColumnsTable({ title, columns }) {
  return (
    <div className="mx-3 mb-3 small">
      <p className="fw-bold mb-1">{title}</p>
      <Table size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Spalte</th>
            <th>Metafeld</th>
            <th>Übernommen</th>
            <th>Geleert</th>
            <th>Unbekannt</th>
            <th>Fremde Firma</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col) => (
            <tr key={col.label}>
              <td>{col.label}</td>
              <td><code>{col.key}</code></td>
              <td>{col.written}</td>
              <td>{col.cleared}</td>
              <td>{col.skippedUnknownKey}</td>
              <td>{col.skippedOtherFirma}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

MetaColumnsTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    key: PropTypes.string,
    written: PropTypes.number,
    cleared: PropTypes.number,
    skippedUnknownKey: PropTypes.number,
    skippedOtherFirma: PropTypes.number,
  })).isRequired,
}

function CSVImporter() {
  // Definition der schönen Labels für die Datenbankfelder
  const fieldLabels = {
    'vorname': 'Vorname',
    'nachname': 'Nachname',
    'geburtstag': 'Geburtstag',
    'email': 'E-Mail',
    'firma': 'Firma',
    'bereich': 'Tätigkeitsbereich',
    'bezeichnung': 'Bezeichnung',
    'typ': 'Typ',
    'kommentar': 'Kommentar',
    'externe_mitarbeiter_id': 'Externe Mitarbeiter-ID'
  };

  // Schritt des Importprozesses (1: Dateiupload, 2: Firmenauswahl, 3: Vorschau)
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState('mitarbeiter');
  const [, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [firmen, setFirmen] = useState([]);
  const [taetigkeitsbereicheProFirma, setTaetigkeitsbereicheProFirma] = useState({});
  const [selectedFirma, setSelectedFirma] = useState('');
  const [groupedData, setGroupedData] = useState({});
  const [firmaAssignments, setFirmaAssignments] = useState({});
  const [taetigkeitsbereichAssignments, setTaetigkeitsbereichAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  
  // State für teilweise übereinstimmende Firmen
  const [partialMatches, setPartialMatches] = useState({});
  
  // State für teilweise übereinstimmende Tätigkeitsbereiche
  const [partialTaetigkeitsbereichMatches, setPartialTaetigkeitsbereichMatches] = useState({});
  
  // State für Bulk-Editing
  const [selectedMitarbeiter, setSelectedMitarbeiter] = useState([]);
  const [selectedMitarbeiterProFirma, setSelectedMitarbeiterProFirma] = useState({});

  // State für Import-Ergebnisse
  const [importResult, setImportResult] = useState(null);
  
  // State für externe ID Spalte
  const [externalIdColumn, setExternalIdColumn] = useState('');

  // Synchronisations-Modus
  const [syncMode, setSyncMode] = useState(false);
  
  // Filter für die Vorschau
  const [statusFilter, setStatusFilter] = useState('all');

  // Erweiterte Felder: erkannte Meta-Spalten — NUR manuell (Auto-Map bleibt unangetastet)
  const [metaSettingsList, setMetaSettingsList] = useState([]);
  // Erweiterte Felder sind default abgewählt (Symmetrie zum Export-Standard:
  // optionale Felder nur bewusst dazu — je Haken lädt die Preview neu)
  const [metaInclude, setMetaInclude] = useState({});
  const [metaAssigns, setMetaAssigns] = useState({});
  // Doppelte Header der rohen CSV-Zeile: Papa benennt still um (X_1),
  // deshalb beweist nur die unparste Kopfzeile den Doppel-Befund
  const [headerDupes, setHeaderDupes] = useState([]);

  // State für Preview-Daten
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // State für Sortierung
  const [sortConfig, setSortConfig] = useState({ key: 'nachname', direction: 'asc' });

  // Funktion zum Anfordern der Sortierung
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Funktion zum Sortieren der Mitarbeiterdaten
  const sortedCsvData = React.useMemo(() => {
    if (!csvData.length) return [];
    
    const sortableData = [...csvData];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Hole die tatsächlichen Spaltennamen aus dem Mapping
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Wenn das Feld gemappt ist, hole den Wert aus dem gemappten Feld
        const mappedKey = Object.keys(mapping).find(key => mapping[key] === sortConfig.key);
        if (mappedKey) {
          aValue = a[mappedKey];
          bValue = b[mappedKey];
        }
        
        // Behandle leere Werte
        if (aValue === null || aValue === undefined || aValue === '') return 1;
        if (bValue === null || bValue === undefined || bValue === '') return -1;
        
        // Konvertiere zu String und entferne Leerzeichen
        aValue = String(aValue).trim();
        bValue = String(bValue).trim();
        
        // Vergleiche Zahlen
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // Vergleiche Datumsangaben
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        
        // Standard String-Vergleich (Groß-/Kleinschreibung ignorieren)
        const comparison = aValue.localeCompare(bValue, 'de', { sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return sortableData;
  }, [csvData, sortConfig, mapping]);

  // Funktion zum Auswählen aller Mitarbeiter in Schritt 2
  const selectAllMitarbeiter = () => {
    const allIndices = csvData.map((_, index) => index);
    setSelectedMitarbeiter(allIndices);
  };

  // Funktion zum Abwählen aller Mitarbeiter in Schritt 2
  const deselectAllMitarbeiter = () => {
    setSelectedMitarbeiter([]);
  };

  // Funktion zum Umschalten der Auswahl eines Mitarbeiters in Schritt 2
  const toggleMitarbeiterSelection = (index) => {
    if (selectedMitarbeiter.includes(index)) {
      setSelectedMitarbeiter(selectedMitarbeiter.filter(i => i !== index));
    } else {
      setSelectedMitarbeiter([...selectedMitarbeiter, index]);
    }
  };

  // Funktion zum Auswählen aller Mitarbeiter einer Firma in Schritt 3
  const selectAllMitarbeiterInFirma = (firmaId) => {
    const mitarbeiterIndices = groupedData[firmaId].mitarbeiter.map(m => m.index);
    setSelectedMitarbeiterProFirma(prev => ({
      ...prev,
      [firmaId]: mitarbeiterIndices
    }));
  };

  // Funktion zum Abwählen aller Mitarbeiter einer Firma in Schritt 3
  const deselectAllMitarbeiterInFirma = (firmaId) => {
    setSelectedMitarbeiterProFirma(prev => {
      const newState = { ...prev };
      delete newState[firmaId];
      return newState;
    });
  };

  // Funktion zum Umschalten der Auswahl eines Mitarbeiters in Schritt 3
  const toggleMitarbeiterSelectionInFirma = (firmaId, mitarbeiterIndex) => {
    setSelectedMitarbeiterProFirma(prev => {
      const currentSelection = prev[firmaId] || [];
      let newSelection;
      
      if (currentSelection.includes(mitarbeiterIndex)) {
        // Mitarbeiter entfernen
        newSelection = currentSelection.filter(i => i !== mitarbeiterIndex);
      } else {
        // Mitarbeiter hinzufügen
        newSelection = [...currentSelection, mitarbeiterIndex];
      }
      
      return {
        ...prev,
        [firmaId]: newSelection
      };
    });
  };

  // Funktion zum Zuweisen eines Tätigkeitsbereichs zu mehreren Mitarbeitern derselben Firma
  const handleMassTaetigkeitsbereichChange = (firmaId, taetigkeitsbereichId, mitarbeiterIndices) => {
    setTaetigkeitsbereichAssignments(prev => {
      const newAssignments = { ...prev };
      mitarbeiterIndices.forEach(index => {
        newAssignments[`${firmaId}-${index}`] = taetigkeitsbereichId;
      });
      return newAssignments;
    });
    
    // Auswahl zurücksetzen
    setSelectedMitarbeiterProFirma(prev => {
      const newState = { ...prev };
      delete newState[firmaId];
      return newState;
    });
  };

  // Funktion zum Zuweisen einer Firma zu mehreren ausgewählten Mitarbeitern
  const handleMassFirmaChange = (firmaId, selectedIndices) => {
    setFirmaAssignments(prev => {
      const newAssignments = { ...prev };
      selectedIndices.forEach(index => {
        newAssignments[index] = firmaId;
      });
      return newAssignments;
    });
    
    // Auswahl zurücksetzen
    setSelectedMitarbeiter([]);
  };

  // Funktion zum Entfernen eines Mitarbeiters aus der Liste
  const removeMitarbeiter = (index) => {
    // Entferne den Mitarbeiter aus den CSV-Daten
    const newCsvData = [...csvData];
    newCsvData.splice(index, 1);
    setCsvData(newCsvData);

    // Entferne den Mitarbeiter aus den Firmenzuweisungen
    const newFirmaAssignments = { ...firmaAssignments };
    delete newFirmaAssignments[index];
    
    // Aktualisiere die Indizes der verbleibenden Mitarbeiter
    Object.keys(newFirmaAssignments).forEach(key => {
      if (key > index) {
        newFirmaAssignments[key - 1] = newFirmaAssignments[key];
        delete newFirmaAssignments[key];
      }
    });
    
    setFirmaAssignments(newFirmaAssignments);
  };

  // Funktion zum Verarbeiten der Datei
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    setFile(uploadedFile);

    // Zurücksetzen aller States
    setCsvData([]);
    setMapping({});
    setExternalIdColumn('');
    setFirmaAssignments({});
    setTaetigkeitsbereichAssignments({});
    setSelectedMitarbeiter([]);
    setSelectedMitarbeiterProFirma({});
    setGroupedData({});
    setMetaInclude({});
    setMetaAssigns({});
    setHeaderDupes([]);

    // Erste (unparste) Kopfzeile gegen doppelte Header prüfen — Papa
    // dedupliziert still mit Suffix (X_1) und wirft keine Warnung,
    // die Spalte würde später lautlos nie matchen
    Papa.parse(uploadedFile, {
      header: false,
      preview: 1,
      complete: (rawResult) => {
        const rawHeaders = ((rawResult.data && rawResult.data[0]) || []).map((cell) => String(cell).trim());
        const dupes = [...new Set(rawHeaders.filter((h, i) => rawHeaders.indexOf(h) !== i))];
        if (dupes.length > 0) {
          setHeaderDupes(dupes);
          toast.error(`Doppelte Spalten in der CSV-Kopfzeile: ${dupes.join(', ')} — bitte die Datei korrigieren.`);
          return;
        }
        parseCsvFile(uploadedFile);
      },
    });
  };

  // Haupt-Parse mit Kopfzeile als Mapping-Grundlage
  const parseCsvFile = (uploadedFile) => {
    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
        // Initialisieren des Mappings mit den CSV-Spalten
        const initialMapping = {};
        results.meta.fields.forEach(field => {
          initialMapping[field] = '';
        });
        setMapping(initialMapping);
        // Automatische Zuordnung direkt nach dem Parsen
        setTimeout(() => autoMapColumns(initialMapping), 0);
      }
    });
  };

  // Funktion zum Aktualisieren des Mappings
  const handleMappingChange = (csvColumn, dbColumn) => {
    setMapping(prev => {
      const newMapping = { ...prev };
      
      // Wenn externe_mitarbeiter_id ausgewählt wird, setze externalIdColumn
      if (dbColumn === 'externe_mitarbeiter_id') {
        setExternalIdColumn(csvColumn);
      }
      
      // Wenn externe_mitarbeiter_id deselektiert wird, reset externalIdColumn
      if (prev[csvColumn] === 'externe_mitarbeiter_id' && dbColumn !== 'externe_mitarbeiter_id') {
        setExternalIdColumn('');
      }
      
      // Entferne vorherige Zuordnung für diese CSV-Spalte
      if (dbColumn === '') {
        delete newMapping[csvColumn];
      } else {
        newMapping[csvColumn] = dbColumn;
      }
      
      return newMapping;
    });
  };

  // Funktion zum Ändern der Firma eines einzelnen Mitarbeiters
  const handleFirmaChange = (index, firmaId) => {
    setFirmaAssignments(prev => ({
      ...prev,
      [index]: firmaId
    }));
  };

  // Funktion zum Ändern des Tätigkeitsbereichs eines Mitarbeiters
  const handleTaetigkeitsbereichChange = (firmaId, mitarbeiterIndex, taetigkeitsbereichId) => {
    setTaetigkeitsbereichAssignments(prev => ({
      ...prev,
      [`${firmaId}-${mitarbeiterIndex}`]: taetigkeitsbereichId
    }));
  };

  // Funktion zum Fortfahren zum nächsten Schritt
  const nextStep = async () => {
    if (step === 1 && csvData.length > 0) {
      // Automatisches Mapping nur durchführen, wenn noch keine manuellen Änderungen vorgenommen wurden
      const hasManualMapping = Object.values(mapping).some(value => value !== '');
      if (!hasManualMapping) {
        autoMapColumns();
      } else {
        // Aber die automatische Firma-Zuordnung trotzdem durchführen
        autoAssignFirmen();
      }
    }
    
    // Wenn wir von Schritt 2 zu Schritt 3 wechseln, laden wir die Tätigkeitsbereiche
    if (step === 2) {
      setLoading(true);
      // Identifiziere die verwendeten Firmen
      const usedFirmenIds = [...new Set(Object.values(firmaAssignments))];
      
      // Lade die Tätigkeitsbereiche für jede verwendete Firma
      for (const firmaId of usedFirmenIds) {
        if (firmaId) {
          await fetchTaetigkeitsbereiche(firmaId);
        }
      }
      setLoading(false);
    }
    
    setStep(step + 1);
  };

  // Funktion zum Zurückgehen zum vorherigen Schritt
  const prevStep = () => {
    setStep(step - 1);
  };

  // Funktion zur automatischen Zuordnung von CSV-Spalten zu Datenbankfeldern
  const autoMapColumns = (currentMapping = mapping) => {
    const newMapping = { ...currentMapping };
    
    // Definition der möglichen Zuordnungen für Mitarbeiter
    const mitarbeiterMappings = {
      'externe_mitarbeiter_id': ['Externe Mitarbeiter-ID', 'Externe ID', 'External ID', 'External Employee ID'],
      'nachname': ['nachname', 'Nachname', 'Nachname*', 'last name', 'lastname'],
      'vorname': ['vorname', 'Vorname', 'Vorname*', 'first name', 'firstname'],
      'geburtstag': ['geburtstag', 'Geburtstag', 'Geburtsdatum', 'birthday', 'birth date', 'date of birth'],
      'email': ['email', 'Email', 'E-Mail', 'E-Mail*', 'mail'],
      'firma': ['firma', 'Firma', 'company', 'firm'],
      'bereich': ['bereich', 'Bereich', 'department', 'division', 'taetigkeitsbereich', 'tätigkeitsbereich']
    };
    
    // Definition der mögliche Zuordnungen für Tätigkeitsbereiche
    const taetigkeitsbereichMappings = {
      'bezeichnung': ['bezeichnung', 'Bezeichnung', 'Bezeichnung*', 'designation', 'title'],
      'typ': ['typ', 'Typ', 'Type', 'category'],
      'kommentar': ['kommentar', 'Kommentar', 'comment', 'description', 'beschreibung']
    };
    
    // Wähle die entsprechenden Mappings basierend auf der Entität
    const mappings = entity === 'mitarbeiter' ? mitarbeiterMappings : taetigkeitsbereichMappings;
    
    // Durchlaufe alle CSV-Spalten und versuche eine automatische Zuordnung
    Object.keys(newMapping).forEach(csvColumn => {
      // Prüfe auf direkte Übereinstimmung (Groß-/Kleinschreibung ignorieren)
      const csvColumnLower = csvColumn.toLowerCase().trim();
      
      // Suche nach passendem Datenbankfeld
      for (const [dbField, possibleNames] of Object.entries(mappings)) {
        if (possibleNames.some(name => name.toLowerCase() === csvColumnLower)) {
          newMapping[csvColumn] = dbField;
          if (dbField === 'externe_mitarbeiter_id') {
            setExternalIdColumn(csvColumn);
          }
          break;
        }
      }
    });
    
    setMapping(newMapping);
    
    // Automatische Vorabzuordnung von Firmen und Tätigkeitsbereichen
    if (entity === 'mitarbeiter') {
      autoAssignFirmen(newMapping);
      autoAssignTaetigkeitsbereiche(newMapping);
    }
  };

  // Funktion zum Abrufen der Tätigkeitsbereiche von der API
  const fetchTaetigkeitsbereiche = async (firmaId) => {
    try {
      const response = await fetchApi(`${ZUORDNUNG}?firmenId=${firmaId}`);
      // Prüfen, ob die Antwort ein Array ist
      let bereiche = [];
      if (Array.isArray(response)) {
        // Filtere nur die Tätigkeitsbereiche, die zur ausgewählten Firma gehören
        bereiche = response.filter(bereich => 
          bereich.firmenId == firmaId || (bereich.firmen && bereich.firmen.id == firmaId)
        );
      } else if (response && Array.isArray(response.data)) {
        // Wenn die Antwort ein Objekt mit data-Eigenschaft ist
        bereiche = response.data.filter(bereich => 
          bereich.firmenId == firmaId || (bereich.firmen && bereich.firmen.id == firmaId)
        );
      } else {
        console.warn('Tätigkeitsbereich-Daten sind kein Array:', response);
      }
      
      // Speichere die Tätigkeitsbereiche für diese Firma
      setTaetigkeitsbereicheProFirma(prev => ({
        ...prev,
        [firmaId]: bereiche
      }));
      
      return bereiche;
    } catch (error) {
      console.error('Fehler beim Abrufen der Tätigkeitsbereiche:', error);
      toast.error('Fehler beim Abrufen der Tätigkeitsbereiche');
      return [];
    }
  };

  // Funktion zum Abrufen der Firmen von der API
  const fetchFirmen = async () => {
    try {
      const response = await fetchApi('firmen');
      // Die Antwort hat eine andere Struktur, wir müssen auf die data-Eigenschaft zugreifen
      if (response && Array.isArray(response.data)) {
        setFirmen(response.data);
      } else {
        console.warn('Firmendaten sind kein Array:', response);
        setFirmen([]);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Firmen:', error);
      toast.error('Fehler beim Abrufen der Firmen');
      setFirmen([]); // Setze auf leeres Array im Fehlerfall
    }
  };

  // Funktion zur automatischen Zuordnung von Firmen
  const autoAssignFirmen = (currentMapping = mapping) => {
    // Automatische Zuordnung von Firmen nur durchführen, wenn eine Firmenspalte vorhanden ist
    // und firmen ein Array ist
    if (!Array.isArray(firmen)) {
      console.warn('Firmen ist kein Array:', firmen);
      return;
    }
    
    const firmaColumn = Object.keys(currentMapping).find(key => currentMapping[key] === 'firma');
    if (firmaColumn) {
      const newFirmaAssignments = {};
      const newPartialMatches = {};
      csvData.forEach((row, index) => {
        const firmaName = row[firmaColumn];
        if (firmaName) {
          // Suche nach passender Firma in der Firmenliste (exakte Übereinstimmung)
          const matchingFirma = firmen.find(f => 
            f.name.toLowerCase() === firmaName.toLowerCase().trim()
          );
          if (matchingFirma) {
            newFirmaAssignments[index] = matchingFirma.id;
          } else {
            // Suche nach teilweise übereinstimmenden Firmen
            const partialMatchingFirma = firmen.find(f => 
              f.name.toLowerCase().includes(firmaName.toLowerCase().trim()) ||
              firmaName.toLowerCase().trim().includes(f.name.toLowerCase())
            );
            if (partialMatchingFirma) {
              newFirmaAssignments[index] = partialMatchingFirma.id;
              newPartialMatches[index] = {
                csvName: firmaName,
                dbName: partialMatchingFirma.name
              };
            }
          }
        }
      });
      setFirmaAssignments(newFirmaAssignments);
      setPartialMatches(newPartialMatches);
    } else {
      // Wenn keine Firmenspalte vorhanden ist, leere Zuordnungen setzen
      setFirmaAssignments({});
      setPartialMatches({});
    }
  };

  // Funktion zur automatischen Zuordnung von Tätigkeitsbereichen
  const autoAssignTaetigkeitsbereiche = (currentMapping = mapping) => {
    if (entity === 'mitarbeiter') {
      // Automatische Zuordnung von Tätigkeitsbereichen
      const bereichColumn = Object.keys(currentMapping).find(key => currentMapping[key] === 'bereich');
      if (bereichColumn) {
        const newTaetigkeitsbereichAssignments = {};
        // Wir können die Tätigkeitsbereiche noch nicht endgültig zuordnen, da wir die Firma noch nicht kennen
        // Aber wir können eine Vorabzuordnung machen, die später in Schritt 3 vervollständigt wird
        csvData.forEach((row, index) => {
          const bereichName = row[bereichColumn];
          if (bereichName) {
            // Suche nach passendem Tätigkeitsbereich in der Liste (exakte Übereinstimmung)
            // Da wir noch nicht wissen, welche Firma zugewiesen wird, können wir hier noch nicht filtern
            // Wir verwenden einfach den ersten gefundenen Bereich mit diesem Namen
            let matchingBereich = null;
            for (const firmaId in taetigkeitsbereicheProFirma) {
              const bereiche = taetigkeitsbereicheProFirma[firmaId];
              matchingBereich = bereiche.find(b => 
                b.bezeichnung.toLowerCase() === bereichName.toLowerCase().trim()
              );
              if (matchingBereich) break;
            }
            
            if (matchingBereich) {
              // Speichere die gefundene ID als Vorabzuordnung
              newTaetigkeitsbereichAssignments[`temp-${index}`] = matchingBereich.id;
            }
          }
        });
        // Wir setzen die Vorabzuordnungen nicht direkt in setTaetigkeitsbereichAssignments,
        // da wir die endgültige Zuordnung später in Schritt 3 machen werden
      }
    }
  };

  // Funktion zum Gruppieren der Daten nach Firmen
  const groupDataByFirma = async () => {
    // Stelle sicher, dass firmen ein Array ist
    if (!Array.isArray(firmen)) {
      console.warn('Firmen ist kein Array:', firmen);
      return;
    }
    
    const grouped = {};
    const newPartialTaetigkeitsbereichMatches = {};
    const newTaetigkeitsbereichAssignments = { ...taetigkeitsbereichAssignments };
    
    // Gruppiere die Daten nach Firmen
    csvData.forEach((row, index) => {
      const firmaId = firmaAssignments[index];
      if (firmaId) {
        if (!grouped[firmaId]) {
          grouped[firmaId] = {
            firma: firmen.find(f => f.id == firmaId),
            mitarbeiter: []
          };
        }
        grouped[firmaId].mitarbeiter.push({ ...row, index });
        
        // Übernehme Vorabzuordnungen von Tätigkeitsbereichen
        const tempKey = `temp-${index}`;
        if (newTaetigkeitsbereichAssignments[tempKey]) {
          newTaetigkeitsbereichAssignments[`${firmaId}-${index}`] = newTaetigkeitsbereichAssignments[tempKey];
          delete newTaetigkeitsbereichAssignments[tempKey];
        }
      }
    });
    
    setGroupedData(grouped);
    
    // Automatische Zuordnung von Tätigkeitsbereichen in Schritt 3
    const bereichColumn = Object.keys(mapping).find(key => mapping[key] === 'bereich');
    
    if (bereichColumn) {
      // Für jede Firma die entsprechenden Tätigkeitsbereiche laden und Zuordnungen vornehmen
      for (const [firmaId, firmaData] of Object.entries(grouped)) {
        // Die Tätigkeitsbereiche wurden bereits in nextStep geladen, daher verwenden wir sie direkt
        firmaData.mitarbeiter.forEach((mitarbeiter) => {
          const bereichName = mitarbeiter[bereichColumn];
          if (bereichName) {
            // Hole die Tätigkeitsbereiche für diese Firma
            const firmaBereiche = taetigkeitsbereicheProFirma[firmaId] || [];
            
            // Suche nach passendem Tätigkeitsbereich in der Liste (exakte Übereinstimmung)
            const matchingBereich = firmaBereiche.find(b => 
              b.bezeichnung.toLowerCase() === bereichName.toLowerCase().trim()
            );
            if (matchingBereich) {
              // Speichere die gefundene ID als Zuordnung
              newTaetigkeitsbereichAssignments[`${firmaId}-${mitarbeiter.index}`] = matchingBereich.id;
            } else {
              // Suche nach teilweise übereinstimmenden Tätigkeitsbereichen
              const partialMatchingBereich = firmaBereiche.find(b => 
                b.bezeichnung.toLowerCase().includes(bereichName.toLowerCase().trim()) ||
                bereichName.toLowerCase().trim().includes(b.bezeichnung.toLowerCase())
              );
              if (partialMatchingBereich) {
                newTaetigkeitsbereichAssignments[`${firmaId}-${mitarbeiter.index}`] = partialMatchingBereich.id;
                newPartialTaetigkeitsbereichMatches[`${firmaId}-${mitarbeiter.index}`] = {
                  csvName: bereichName,
                  dbName: partialMatchingBereich.bezeichnung
                };
              }
            }
          }
        });
        
        // Aktualisiere die States nach jeder Firma
        setPartialTaetigkeitsbereichMatches({ ...newPartialTaetigkeitsbereichMatches });
        setTaetigkeitsbereichAssignments({ ...newTaetigkeitsbereichAssignments });
      }
    }
    
    // WICHTIG: Gebe die gruppierten Daten zurück für die Preview
    return grouped;
  };

  // Effekt zum Gruppieren der Daten und Laden der Preview, wenn der Schritt 3 erreicht wird
  useEffect(() => {
    if (step === 3) {
      setLoading(true);
      // Rufe die asynchrone Funktion mit einer IIFE auf
      (async () => {
        // Gruppiere Daten und hole das Ergebnis direkt
        const grouped = await groupDataByFirma();
        // Direkt Preview laden mit den gruppierten Daten
        if (entity === 'mitarbeiter' && grouped && Object.keys(grouped).length > 0) {
          try {
            const response = await saveApi('import/mitarbeiter/preview', {
              groupedData: grouped,
              mapping,
              externalIdColumn,
              syncMode,
              metaColumns: metaPreviewColumns.length > 0 ? metaPreviewColumns : undefined
            });
            if (response) {
              setPreviewData(response);
              setShowPreview(true);
            }
          } catch (error) {
            console.error('Fehler beim Laden der Preview:', error);
            toast.error('Fehler beim Laden der Preview: ' + error.message);
          }
        }
        setLoading(false);
      })();
    }
  }, [step]);

  // Metafeld-Definitionen für „Erweiterte Felder" — einmalig pro Wizard-Lauf;
  // schon nach dem Upload laden, damit Schritt 1 die erkannten Spalten markieren kann
  useEffect(() => {
    if (
      entity === 'mitarbeiter' &&
      metaSettingsList.length === 0 &&
      (step === 3 || csvData.length > 0)
    ) {
      (async () => {
        try {
          const response = await fetchApi('metaSettings');
          if (response && Array.isArray(response.data)) {
            setMetaSettingsList(response.data);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Metafelder:', error);
          toast.error('Fehler beim Laden der Metafelder: ' + error.message);
        }
      })();
    }
  }, [step, entity, metaSettingsList.length, csvData.length]);

  // Erkannte Meta-Spalten der CSV: nur exakte Label/Key-Treffer im Run-Scope
  // (global + zugewiesene Firmen); Doppel-Label wird rot markiert.
  const metaCandidates = React.useMemo(() => {
    if (entity !== 'mitarbeiter' || metaSettingsList.length === 0) return [];
    // Ohne Firma-Vorabzuordnung (Schritt 1, autoAssignFirmen sieht stale csvData)
    // gegen ALLE Definitionen erkennen — der Server-Run-Scope entscheidet später:
    // Werte einer fremden Firma schreibt der Server nicht (Skip je DB-firmenId).
    const assigned = Object.keys(firmaAssignments).length > 0;
    const runFirmaIds = new Set(
      Object.values(firmaAssignments).map(Number),
    );
    const runDefs = metaSettingsList.filter(
      (d) =>
        d.firmenId === null ||
        !assigned ||
        runFirmaIds.has(Number(d.firmenId)),
    );
    const sourceOf = (key) => {
      const defs = runDefs.filter((d) => d.key === key);
      const winning = defs.find((d) => d.firmenId !== null) || defs[0];
      if (!winning) return '';
      if (winning.firmenId === null) return 'Global';
      const firma = firmen.find((f) => Number(f.id) === Number(winning.firmenId));
      return firma ? `Firma ${firma.name}` : `Firma ${winning.firmenId}`;
    };
    return Object.keys(mapping)
      .filter((header) => !mapping[header])
      .reduce((acc, header) => {
        const pinned = metaAssigns[header];
        const labelKeys = [
          ...new Set(
            runDefs.filter((d) => d.label === header).map((d) => d.key),
          ),
        ];
        let keys;
        if (pinned) {
          keys = [pinned];
        } else if (labelKeys.length > 0) {
          keys = labelKeys;
        } else if (runDefs.some((d) => d.key === header)) {
          keys = [header];
        } else {
          keys = [];
        }
        if (keys.length === 0) return acc;
        acc.push({
          header,
          key: keys[0],
          ambiguous: keys.length > 1,
          options: keys.map((k) => ({ key: k, source: sourceOf(k) })),
          source: sourceOf(keys[0]),
        });
        return acc;
      }, []);
  }, [entity, metaSettingsList, mapping, firmaAssignments, metaAssigns, firmen]);
  const metaCandidateByHeader = React.useMemo(
    () => new Map(metaCandidates.map((c) => [c.header, c])),
    [metaCandidates],
  );

  // Erweiterte Felder sind opt-in: nur bewusste Haken landen im Payload.
  const metaIncluded = metaCandidates.filter((c) => metaInclude[c.header] === true);
  const metaPreviewColumns = metaIncluded
    .filter((c) => !c.ambiguous)
    .map((c) => ({ header: c.header, key: c.key }));
  // Zwei eingeschlossene Spalten auf denselben Key: der Server würde 400en —
  // hier früher blockieren, der Fehler sagt die Spalte.
  const metaKeyConflict = [...metaPreviewColumns.reduce((acc, c) => {
    acc.set(c.key, (acc.get(c.key) || 0) + 1);
    return acc;
  }, new Map())].some(([, n]) => n > 1);
  const metaBlocking = metaIncluded.some((c) => c.ambiguous) || metaKeyConflict;

  // Funktion zum Laden der Preview-Daten
  const loadPreview = async (force = false) => {
    if (entity === 'mitarbeiter' && (!showPreview || force)) {
      try {
        setLoading(true);
        const response = await saveApi('import/mitarbeiter/preview', {
          groupedData,
          mapping,
          externalIdColumn,
          syncMode,
          metaColumns: metaPreviewColumns.length > 0 ? metaPreviewColumns : undefined,
        });
        
        if (response) {
          setPreviewData(response);
          setShowPreview(true);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Preview:', error);
        toast.error('Fehler beim Laden der Preview: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Preview neu laden, wenn die Meta-Feldauswahl im Schritt 3 geändert wird
  useEffect(() => {
    if (step === 3 && entity === 'mitarbeiter' && Object.keys(groupedData).length > 0 && previewData) {
      loadPreview(true);
    }
  }, [metaInclude, metaAssigns, metaSettingsList]);

  // Erweiterte Felder: Einschluss umschalten (opt-in — erst der Haken schreibt Werte)
  const handleMetaInclude = (header) => {
    setMetaInclude((prev) => ({ ...prev, [header]: !(prev[header] === true) }));
  };

  // Nutzerauflösung mehrdeutiger Labels — der Server prüft exakt nach
  const handleMetaAssign = (header, key) => {
    setMetaAssigns((prev) => {
      const next = { ...prev };
      if (key) {
        next[header] = key;
      } else {
        delete next[header];
      }
      return next;
    });
  };

  // Funktion zum Starten des Imports
  const startImport = async () => {
    if (entity === 'mitarbeiter') {
      // Mitarbeiter-Import
      try {
        // Pins eingeschlossener Kandidaten (auch nach Auflösung; Server prüft exakt)
        const metaHeaderMapPayload = Object.fromEntries(
          metaIncluded
            .filter((c) => metaAssigns[c.header])
            .map((c) => [c.header, c.key]),
        );
        const metaValuesPayload = {};
        if (metaPreviewColumns.length > 0) {
          csvData.forEach((row, index) => {
            const entry = {};
            for (const c of metaPreviewColumns) {
              entry[c.header] = row[c.header] == null ? '' : String(row[c.header]).trim();
            }
            metaValuesPayload[index] = entry;
          });
        }
        const response = await saveApi('import/mitarbeiter', {
          groupedData,
          mapping,
          firmaAssignments,
          taetigkeitsbereichAssignments,
          externalIdColumn,
          syncMode,
          metaValues: metaPreviewColumns.length > 0 ? metaValuesPayload : undefined,
          metaHeaderMap: Object.keys(metaHeaderMapPayload).length > 0 ? metaHeaderMapPayload : undefined
        });
        
        if (response) {
          // Zeige Toast-Meldung mit neuen Statistiken
          const stats = response.stats || {};
          const totalProcessed = (stats.created || 0) + (stats.updated || 0) + (stats.reactivated || 0) + (stats.archived || 0);
          let message = `Import abgeschlossen! ${totalProcessed} Aktionen durchgeführt.`;
          
          const details = [];
          if (stats.created) details.push(`${stats.created} neu`);
          if (stats.updated) details.push(`${stats.updated} aktualisiert`);
          if (stats.reactivated) details.push(`${stats.reactivated} reaktiviert`);
          if (stats.archived) details.push(`${stats.archived} archiviert`);
          
          if (stats.metaRowsIgnored) {
            details.push(`${stats.metaRowsIgnored} Meta-Zeilen ohne Firmenzuordnung ignoriert`);
          }
          if (details.length > 0) {
            message += ` (${details.join(', ')})`;
          }
          
          if (totalProcessed === 0) {
            toast.warn(`${message}, ${stats.skipped || 0} übersprungen.`);
          } else {
            toast.success(message);
          }
          
          // Setze das Import-Ergebnis (nur für Fehlerdetails)
          setImportResult(response);
          
          // Zurück zum Start
          setStep(1);
          setFile(null);
          setCsvData([]);
          setMapping({});
          setSelectedFirma('');
          setGroupedData({});
          setFirmaAssignments({});
          setTaetigkeitsbereichAssignments({});
          setExternalIdColumn('');
          setPreviewData(null);
          setShowPreview(false);
          setMetaInclude({});
          setMetaAssigns({});
          setHeaderDupes([]);
          
          // File Input zurücksetzen
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          // Setze das Import-Ergebnis mit Fehler
          setImportResult({ error: 'Unbekannter Fehler beim Import' });
        }
      } catch (error) {
        console.error('Fehler beim Import:', error);
        toast.error('Fehler beim Import: ' + error.message);
        setImportResult({ error: error.message });
      }
    } else {
      // Tätigkeitsbereich-Import
      try {
        // Extrahiere den Firmennamen aus den CSV-Daten, wenn eine Firmenspalte gemappt ist
        let firmaName = null;
        let firmaId = null;
        const firmaColumn = Object.keys(mapping).find(key => mapping[key] === 'firma');
        if (firmaColumn && csvData.length > 0) {
          firmaName = csvData[0][firmaColumn];
          // Entferne Leerzeichen am Anfang und Ende
          if (firmaName) {
            firmaName = firmaName.trim();
            // Suche die entsprechende Firmen-ID
            const matchingFirma = firmen.find(f => 
              f.name.toLowerCase() === firmaName.toLowerCase()
            );
            if (matchingFirma) {
              firmaId = matchingFirma.id;
            }
          }
        }
        
        // Erstelle importData mit den gemappten Daten und der firmenId
        const importData = csvData.map((row, index) => {
          const mappedRow = {};
          
          // Wende das Mapping an
          Object.keys(mapping).forEach(csvColumn => {
            const dbColumn = mapping[csvColumn];
            if (dbColumn && dbColumn !== '') {
              mappedRow[dbColumn] = row[csvColumn];
            }
          });
          
          // Verwende die bereits zugeordnete Firma aus Schritt 2
          mappedRow.firmenId = firmaAssignments[index] || null;
          
          return mappedRow;
        });
        
        const response = await saveApi('import/taetigkeitsbereiche', {
          importData,
          firmaId, // Füge die Firmen-ID zum Payload hinzu
          firmaName // Füge auch den Firmennamen zum Payload hinzu (für Debugging)
        });
        
        if (response) {
          // Zeige Toast-Meldung
          if (response.imported === 0) {
            toast.warn(`Import abgeschlossen! ${response.imported} Datensätze importiert, ${response.skipped} übersprungen.`);
          } else {
            toast.success(`Import abgeschlossen! ${response.imported} Datensätze importiert, ${response.skipped} übersprungen.`);
          }
          
          // Setze das Import-Ergebnis (nur für Fehlerdetails)
          setImportResult(response);
          
          // Zurück zum Start
          setStep(1);
          setFile(null);
          setCsvData([]);
          setMapping({});
          
          // File Input zurücksetzen
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          // Setze das Import-Ergebnis mit Fehler
          setImportResult({ error: 'Unbekannter Fehler beim Import' });
        }
      } catch (error) {
        console.error('Fehler beim Import:', error);
        toast.error('Fehler beim Import: ' + error.message);
        setImportResult({ error: error.message });
      }
    }
  };

  // Tätigkeitsbereiche laden, wenn die Entität Mitarbeiter ist
  useEffect(() => {
    if (entity === 'mitarbeiter') {
      // Wenn bereits eine Firma ausgewählt ist, laden wir die Tätigkeitsbereiche für diese Firma
      if (selectedFirma) {
        fetchTaetigkeitsbereiche(selectedFirma);
      }
    }
  }, [entity, selectedFirma]);

  // Referenzen für Drag & Drop
  const dropAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const fileInfoRef = useRef(null);
  const uploadBtnRef = useRef(null);

  // Firmen beim Start laden
  useEffect(() => {
    fetchFirmen();
  }, []);

  // Funktion zum Überprüfen der Dateiendung
  const isCsvFile = (file) => {
    const acceptedTypes = ['.csv'];
    const fileName = file.name.toLowerCase();
    return acceptedTypes.some(type => fileName.endsWith(type));
  };

  // Effekt für Drag & Drop Funktionalität
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    const fileInput = fileInputRef.current;
    const fileInfo = fileInfoRef.current;
    const uploadBtn = uploadBtnRef.current;

    if (!dropArea || !fileInput || !fileInfo || !uploadBtn) return;

    // Button-Event
    const handleUploadBtnClick = () => {
      fileInput.click();
    };

    uploadBtn.addEventListener('click', handleUploadBtnClick);

    // Datei-Input-Event
    const handleFileInputChange = (e) => {
      handleFileSelection(e);
    };

    fileInput.addEventListener('change', handleFileInputChange);

    // Drag & Drop Events
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const highlight = () => {
      dropArea.classList.add('highlight');
    };

    const unhighlight = () => {
      dropArea.classList.remove('highlight');
    };

    // Funktion zum Überprüfen, ob die gezogene Datei akzeptiert wird
    const isDragFileAccepted = (items) => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && !isCsvFile(file)) {
            return false;
          }
        }
      }
      return true;
    };

    // Funktion zum Hinzufügen von visuellem Feedback beim Drag-Over
    const highlightAccept = (e) => {
      if (isDragFileAccepted(e.dataTransfer.items)) {
        dropArea.classList.add('highlight');
        dropArea.classList.remove('highlight-reject');
      } else {
        dropArea.classList.add('highlight-reject');
        dropArea.classList.remove('highlight');
      }
    };

    // Funktion zum Entfernen des visuellen Feedbacks
    const unhighlightAll = () => {
      dropArea.classList.remove('highlight');
      dropArea.classList.remove('highlight-reject');
    };

    const handleDrop = (e) => {
      const dt = e.dataTransfer;
      const file = dt.files[0];
      
      // Überprüfe, ob die Datei eine CSV-Datei ist
      if (file && isCsvFile(file)) {
        fileInput.files = dt.files;
        handleFileSelection(e);
      } else {
        // Zeige eine Fehlermeldung an
        toast.error('Bitte laden Sie nur CSV-Dateien hoch.');
        unhighlight();
      }
    };

    // Dateiverarbeitung
    const handleFileSelection = (e) => {
      let file;
      if (e.type === 'drop') {
        file = e.dataTransfer.files[0];
      } else {
        file = e.target.files[0];
      }

      if (file) {
        // Überprüfe, ob die Datei eine CSV-Datei ist
        if (!isCsvFile(file)) {
          toast.error('Bitte laden Sie nur CSV-Dateien hoch.');
          return;
        }
        
        fileInfo.textContent = 'Ausgewählte Datei: ';
        const nameStrong = document.createElement('strong');
        nameStrong.textContent = file.name;
        fileInfo.appendChild(nameStrong);
        fileInfo.appendChild(document.createElement('br'));
        const fileSizeInfo = document.createElement('small');
        fileSizeInfo.textContent = `Typ: ${file.type || 'unbekannt'}, Größe: ${Math.round(file.size / 1024)} KB`;
        fileInfo.appendChild(fileSizeInfo);
        
        // Erstelle ein künstliches Ereignisobjekt für handleFileUpload
        const fakeEvent = {
          target: {
            files: e.dataTransfer ? e.dataTransfer.files : e.target.files
          }
        };
        
        // Rufe handleFileUpload direkt auf
        handleFileUpload(fakeEvent);
      }
    };

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, preventDefaults, false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      dropArea.addEventListener(eventName, highlightAccept, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, unhighlightAll, false);
    });

    dropArea.addEventListener('drop', handleDrop);

    // Cleanup
    return () => {
      uploadBtn.removeEventListener('click', handleUploadBtnClick);
      fileInput.removeEventListener('change', handleFileInputChange);
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.removeEventListener(eventName, preventDefaults, false);
      });
      ['dragenter', 'dragover'].forEach(eventName => {
        dropArea.removeEventListener(eventName, highlight, false);
      });
      ['dragleave', 'drop'].forEach(eventName => {
        dropArea.removeEventListener(eventName, unhighlight, false);
      });
      dropArea.removeEventListener('drop', handleDrop);
    };
  }, []);

  return (<>
      {/* Fortschrittsanzeige */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between">
            <div className={`flex-fill text-center ${step >= 1 ? 'fw-bold' : ''}`}>1. Dateiupload</div>
            <div className={`flex-fill text-center ${step >= 2 ? 'fw-bold' : ''}`}>2. Firmenauswahl</div>
            <div className={`flex-fill text-center ${step >= 3 ? 'fw-bold' : ''}`}>3. Vorschau & Zuordnung</div>
          </div>
          <Progress value={(step / 3) * 100} />
        </Col>
      </Row>
      
      <Row>
        <Col>
          {/* Schritt 1: Dateiupload und Spalten-Mapping */}
          {step === 1 && (
            <div>
              <Card className="mb-4">
                <CardHeader tag="h4">Entität auswählen</CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label>Entität:</Label>
                    <Input type="select" className="form-control" value={entity} onChange={(e) => setEntity(e.target.value)}>
                      <option value="mitarbeiter">Mitarbeiter</option>
                      <option value="taetigkeitsbereiche">Tätigkeitsbereiche</option>
                    </Input>
                  </div>
                  {entity === 'mitarbeiter' && (
                    <div className="mt-3 border-top pt-3" style={{ marginLeft: '20px' }}>
                      <Label check className="fw-bold" style={{ cursor: 'pointer' }}>
                        <Input 
                          type="checkbox" 
                          checked={syncMode} 
                          onChange={(e) => setSyncMode(e.target.checked)} 
                          className="mr-2"
                        />
                        Synchronisations-Modus
                      </Label>
                      <p className="text-muted small mb-0 ml-4">
                        Mitarbeiter im System archivieren, die nicht in der CSV enthalten sind (Full-Sync).
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
              
              <Card className="mb-4">
                <CardHeader tag="h4">CSV-Datei hochladen</CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label>Datei auswählen:</Label>
                    <div className="drop-area" ref={dropAreaRef}>
                      <p>Ziehe Dateien hierher oder klicke auf den Button</p>
                      <button className="upload-btn" ref={uploadBtnRef} type="button">
                        <span>📁</span> Datei auswählen
                      </button>
                      <div className="file-info" ref={fileInfoRef}></div>
                      <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} />
                    </div>
                    <small className="form-text text-muted">Bitte nur CSV-Dateien hochladen.</small>
                  </div>
                </CardBody>
              </Card>
              
                  {/* Doppelte CSV-Header blockieren: Papa benennt still um (X_1),
                      die Zweit-Spalte wäre später lautlos nie matchbar */}
                  {headerDupes.length > 0 && (
                    <div className="alert alert-danger">
                      <strong>Doppelte CSV-Spalten erkannt:</strong> Die Kopfzeile enthält
                      {'"' + headerDupes.join('", "') + '"'} doppelt. Bitte die Datei korrigieren und
                      erneut hochladen — der Import geht nicht weiter.
                    </div>
                  )}

              {csvData.length > 0 && (
                <Card className="mb-4">
                  <CardHeader tag="h4">Spalten-Mapping</CardHeader>
                  <CardBody>
                    <p>Bitte ordnen Sie die CSV-Spalten den entsprechenden Datenbankfeldern zu:</p>
                    <div className="table-responsive">
                    <Table className="table-striped">
                      <thead>
                        <tr>
                          <th>CSV-Spalte</th>
                          <th>Datenbank-Feld</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(mapping).map((csvColumn) => {
                          const metaCand = entity === 'mitarbeiter' && !mapping[csvColumn]
                            ? metaCandidateByHeader.get(csvColumn)
                            : null;
                          return (
                          <tr key={csvColumn}>
                            <td>{csvColumn}</td>
                            <td>
                              {metaCand ? (
                                <small className={metaCand.ambiguous ? 'text-danger' : 'text-muted'}>
                                  Metafeld <code>{metaCand.key}</code>
                                  {metaCand.ambiguous
                                    ? ' (mehrdeutig) — zuordnen in Schritt 3'
                                    : ' — Übernehmen in Schritt 3'}
                                </small>
                              ) : (
                              <Input 
                                type="select"
                                className="form-control"
                                value={mapping[csvColumn]} 
                                onChange={(e) => handleMappingChange(csvColumn, e.target.value)}
                              >
                                <option value="">Nicht importieren</option>
                                {entity === 'mitarbeiter' ? (
                                  <>
                                    <option value="externe_mitarbeiter_id">Externe Mitarbeiter-ID</option>
                                    <option value="vorname">Vorname</option>
                                    <option value="nachname">Nachname</option>
                                    <option value="geburtstag">Geburtstag</option>
                                    <option value="email">E-Mail</option>
                                    <option value="firma">Firmen Name</option>
                                    <option value="bereich">Tätigkeitsbereich Name</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="bezeichnung">Bezeichnung</option>
                                    <option value="typ">Typ</option>
                                    <option value="kommentar">Kommentar</option>
                                    <option value="firma">Firmen Name</option>
                                  </>
                                )}
                              </Input>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                    {entity === 'mitarbeiter' && metaCandidates.length > 0 && (
                      <div className="alert alert-info small mb-0">
                        {metaCandidates.length} Spalten werden als Metafelder erkannt (Erkennung
                        läuft noch ohne Firmenzuordnung — welche Kandidaten in Schritt 3 gültig
                        bleiben, entscheidet die Firmenzuordnung). Zuordnung in Schritt 3.
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
              
              <div className="d-flex justify-content-end">
                <Button 
                  color="primary"
                  size="lg"
                  onClick={nextStep}
                  disabled={csvData.length === 0 || headerDupes.length > 0}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
          
          {/* Schritt 2: Firmenauswahl */}
          {step === 2 && (
            <div>
              <Card className="mb-4">
                <CardHeader tag="h4">Firmenauswahl</CardHeader>
                <CardBody>
                  <p>Weisen Sie jedem Mitarbeiter eine Firma zu:</p>
                  
                  {/* Massenaktionsleiste für ausgewählte Mitarbeiter (oben) */}
                  {selectedMitarbeiter.length > 0 && (
                    <div className="mb-3 p-2 bg-light border">
                      <span className="mx-2">{selectedMitarbeiter.length} Mitarbeiter ausgewählt</span>
                      <Input 
                        type="select"
                        className="form-control d-inline-block w-auto mx-2"
                        onChange={(e) => {
                          handleMassFirmaChange(e.target.value, selectedMitarbeiter);
                        }}
                      >
                        <option value="">Ausgewählten Mitarbeitern Firma zuweisen</option>
                        {firmen.map((firma) => (
                          <option key={firma.id} value={firma.id}>
                            {firma.name}
                          </option>
                        ))}
                      </Input>
                    </div>
                  )}
                  
                  {/* Info-Meldung für teilweise Übereinstimmungen */}
                  {entity === 'mitarbeiter' && Object.keys(partialMatches).length > 0 && (
                    <div className="alert alert-warning">
                      <strong>Achtung:</strong> Es wurden teilweise Übereinstimmungen bei Firmennamen gefunden. 
                      Bitte überprüfen Sie die Zuordnung sorgfältig.
                      <ul className="mb-0 mt-2">
                        {Object.entries(partialMatches).map(([index, match]) => (
                          <li key={index}>
                            "{match.csvName}" wurde "{match.dbName}" zugeordnet
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Tabelle mit Mitarbeitern und Firmenauswahl */}
                  <div className="table-responsive">
<Table striped>
                      <thead>
                          <tr>
                            <th>
                              <label className="custom-checkbox">
                                <input 
                                  type="checkbox"
                                  className="custom-checkbox-input"
                                  checked={selectedMitarbeiter.length === csvData.length && csvData.length > 0}
                                  onChange={selectedMitarbeiter.length === csvData.length ? deselectAllMitarbeiter : selectAllMitarbeiter}
                                />
                                <span className="custom-checkbox-box"></span>
                              </label>
                            </th>
                            {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'bereich' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                              <th 
                                key={csvColumn} 
                                onClick={() => requestSort(csvColumn)}
                                style={{ cursor: 'pointer' }}
                              >
                                {fieldLabels[mapping[csvColumn]] || mapping[csvColumn] || csvColumn}
                                {sortConfig.key === csvColumn && (
                                  <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                                )}
                              </th>
                            ))}
                            <th>Firma</th>
                            <th>Aktionen</th>
                          </tr>
                      </thead>
                      <tbody>
                        {sortedCsvData.map((row) => {
                          // Finden des ursprünglichen Index in den ungeordneten Daten
                          const originalIndex = csvData.findIndex(originalRow => 
                            originalRow === row
                          );
                          
                          // Prüfen, ob es sich um eine teilweise Übereinstimmung handelt
                          const isPartialMatch = Object.prototype.hasOwnProperty.call(partialMatches, originalIndex);
                          // Finde den CSV-Spaltennamen für die Firma
                          const firmaColumn = Object.keys(mapping).find(key => mapping[key] === 'firma') || '';
                          
                          return (
                            <tr key={originalIndex} style={isPartialMatch ? { backgroundColor: '#fff3cd' } : {}}>
                              <td>
                                <label className="custom-checkbox">
                                  <input 
                                    type="checkbox"
                                    className="custom-checkbox-input"
                                    checked={selectedMitarbeiter.includes(originalIndex)}
                                    onChange={() => toggleMitarbeiterSelection(originalIndex)}
                                  />
                                  <span className="custom-checkbox-box"></span>
                                </label>
                              </td>
                              {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'bereich' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                                <td 
                                  key={csvColumn} 
                                  style={isPartialMatch && csvColumn === firmaColumn ? { backgroundColor: '#fff3cd' } : {}}
                                >
                                  {row[csvColumn]}
                                  {isPartialMatch && csvColumn === firmaColumn && (
                                    <span className="badge bg-warning ml-2">Teilweise Übereinstimmung</span>
                                  )}
                                </td>
                              ))}
                              <td style={isPartialMatch ? { backgroundColor: '#fff3cd' } : {}}>
                                <Input 
                                  type="select"
                                  className="form-control"
                                  value={firmaAssignments[originalIndex] || ''}
                                  onChange={(e) => handleFirmaChange(originalIndex, e.target.value)}
                                >
                                  <option value="">Bitte auswählen</option>
                                  {firmen.map((firma) => (
                                    <option key={firma.id} value={firma.id}>
                                      {firma.name}
                                    </option>
                                  ))}
                                </Input>
                                {isPartialMatch && (
                                  <span className="badge bg-warning ml-2">Teilweise Übereinstimmung</span>
                                )}
                              </td>
                              <td>
                                <Button 
                                  color="danger" 
                                  size="sm" 
                                  onClick={() => removeMitarbeiter(originalIndex)}
                                >
                                  Entfernen
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Massenaktionsleiste für ausgewählte Mitarbeiter (unten) */}
                  {selectedMitarbeiter.length > 0 && (
                    <div className="mt-3 p-2 bg-light border">
                      <span className="mx-2">{selectedMitarbeiter.length} Mitarbeiter ausgewählt</span>
                      <Input 
                        type="select"
                        className="form-control d-inline-block w-auto mx-2"
                        onChange={(e) => {
                          handleMassFirmaChange(e.target.value, selectedMitarbeiter);
                        }}
                      >
                        <option value="">Ausgewählten Mitarbeitern Firma zuweisen</option>
                        {firmen.map((firma) => (
                          <option key={firma.id} value={firma.id}>
                            {firma.name}
                          </option>
                        ))}
                      </Input>
                    </div>
                  )}
                </CardBody>
              </Card>
              
              <div className="d-flex justify-content-between">
                <Button color="secondary" onClick={prevStep}>
                  Zurück
                </Button>
                <div>
                  {loading && (
                    <span className="mr-2">
                      <i className="fas fa-spinner fa-spin"></i> Lade Tätigkeitsbereiche...
                    </span>
                  )}
                  <Button 
                    color="primary"
                    onClick={nextStep}
                    disabled={Object.keys(firmaAssignments).length === 0 || loading}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Schritt 3: Vorschau und Tätigkeitsbereichszuordnung */}
          {step === 3 && (
            <div>
              <Card className="mb-4">
                <CardHeader tag="h4">Vorschau und Tätigkeitsbereichszuordnung</CardHeader>
                <CardBody className="p-0">
                  {/* Loading Indicator */}
                  {loading && (
                    <div className="text-center p-5">
                      <i className="fas fa-spinner fa-spin fa-2x"></i>
                      <p className="mt-2">Daten werden geladen...</p>
                    </div>
                  )}
                  
                  {/* Kompakte Preview-Statistik */}
                  {!loading && entity === 'mitarbeiter' && previewData && (
                    <div className="alert alert-light border m-3 py-2">
                      <div className="d-flex justify-content-center align-items-center flex-wrap">
                        <Button 
                          color={statusFilter === 'all' ? 'primary' : 'outline-primary'} 
                          size="sm" 
                          className="m-1"
                          onClick={() => setStatusFilter('all')}
                        >
                          Alle ({csvData.length + (previewData.stats?.toArchive || 0)})
                        </Button>
                        <Button 
                          color={statusFilter === 'create' ? 'success' : 'outline-success'} 
                          size="sm" 
                          className="m-1"
                          onClick={() => setStatusFilter('create')}
                          disabled={!previewData.stats?.toCreate}
                        >
                          🟢 Neu: {previewData.stats?.toCreate || 0}
                        </Button>
                        <Button 
                          color={statusFilter === 'update' ? 'warning' : 'outline-warning'} 
                          size="sm" 
                          className="m-1"
                          onClick={() => setStatusFilter('update')}
                          disabled={!previewData.stats?.toUpdate}
                        >
                          🟡 Update: {previewData.stats?.toUpdate || 0}
                        </Button>
                        <Button 
                          color={statusFilter === 'reactivate' ? 'info' : 'outline-info'} 
                          size="sm" 
                          className="m-1"
                          onClick={() => setStatusFilter('reactivate')}
                          disabled={!previewData.stats?.toReactivate}
                        >
                          🔵 Reaktivieren: {previewData.stats?.toReactivate || 0}
                        </Button>
                        {syncMode && (
                          <Button 
                            color={statusFilter === 'archive' ? 'secondary' : 'outline-secondary'} 
                            size="sm" 
                            className="m-1"
                            onClick={() => setStatusFilter('archive')}
                            disabled={!previewData.stats?.toArchive}
                            style={statusFilter === 'archive' ? { backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: 'white' } : { color: '#fd7e14', borderColor: '#fd7e14' }}
                          >
                            🟠 Archivieren: {previewData.stats?.toArchive || 0}
                          </Button>
                        )}
                        <Button 
                          color={statusFilter === 'error' ? 'danger' : 'outline-danger'} 
                          size="sm" 
                          className="m-1"
                          onClick={() => setStatusFilter('error')}
                          disabled={!previewData.skipped}
                        >
                          ❌ Fehler: {previewData.skipped || 0}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Meta-Zähler */}
                  {!loading && entity === 'mitarbeiter' && previewData && previewData.metaColumns && previewData.metaColumns.length > 0 && (
                    <MetaColumnsTable
                      title="Metafelder (leere Zelle = Wert wird gelöscht):"
                      columns={previewData.metaColumns}
                    />
                  )}

                  {/* Sicherheitswarnung bei hoher Archivierungsrate */}
                  {!loading && syncMode && previewData?.stats?.toArchive > 0 && (
                    (() => {
                      const totalActive = csvData.length + previewData.stats.toArchive;
                      const archiveRate = (previewData.stats.toArchive / totalActive) * 100;
                      if (archiveRate > 50) {
                        return (
                          <div className="alert alert-danger m-3">
                            <strong>⚠️ Hohe Archivierungsrate!</strong> Sie archivieren {Math.round(archiveRate)}% der Mitarbeiter dieser Auswahl. Bitte prüfen Sie, ob dies beabsichtigt ist.
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                  
                  {/* Fehler-Anzeige */}
                  {!loading && entity === 'mitarbeiter' && previewData && previewData.skipped > 0 && statusFilter === 'error' && (
                    <div className="m-3 alert alert-warning">
                      <strong>Details zu den Fehlern:</strong>
                      {previewData.skippedRecords && previewData.skippedRecords.length > 0 && (
                        <ul className="mb-0 mt-2 small">
                          {previewData.skippedRecords.map((record, idx) => (
                            <li key={idx}>
                              {record.reason} 
                              {record.row && ` (${record.row.vorname || ''} ${record.row.nachname || ''})`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  
                  {/* Info-Meldung für teilweise Übereinstimmungen von Tätigkeitsbereichen */}
                  {entity === 'mitarbeiter' && Object.keys(partialTaetigkeitsbereichMatches).length > 0 && (
                    <div className="alert alert-warning m-3">
                      <strong>Achtung:</strong> Es wurden teilweise Übereinstimmungen bei Tätigkeitsbereichen gefunden. 
                      Bitte überprüfen Sie die Zuordnung sorgfältig.
                    </div>
                  )}
                  
                  {/* Bedingte Darstellung basierend auf der Entität */}
                  {!loading && entity === 'mitarbeiter' ? (
                    // Mitarbeiter-Vorschau mit Firmengruppierung
                    Object.keys(groupedData).length > 0 ? (
                      Object.entries(groupedData).map(([firmaId, firmaData]) => {
                        const selectedMitarbeiterInFirma = selectedMitarbeiterProFirma[firmaId] || [];
                        
                        // Filtere Mitarbeiter basierend auf statusFilter
                        const displayMitarbeiter = statusFilter === 'all' 
                          ? firmaData.mitarbeiter 
                          : firmaData.mitarbeiter.filter(m => previewData?.employeeStatus[m.index] === statusFilter);

                        // Archiv-Kandidaten für diese Firma (nur wenn Filter 'all' oder 'archive')
                        const displayArchives = (syncMode && (statusFilter === 'all' || statusFilter === 'archive'))
                          ? (previewData?.archiveCandidates?.[firmaId] || [])
                          : [];

                        if (displayMitarbeiter.length === 0 && displayArchives.length === 0) return null;

                        return (
                          <div key={firmaId} className="mb-4">
                            <h5 className="bg-light p-2 mb-0">{firmaData.firma.name}</h5>
                            
                            {/* Massenaktionsleiste für ausgewählte Mitarbeiter */}
                            {selectedMitarbeiterInFirma.length > 0 && (
                              <div className="p-2 bg-light border-bottom">
                                <span className="mx-2">{selectedMitarbeiterInFirma.length} Mitarbeiter ausgewählt</span>
                                <Input 
                                  type="select"
                                  className="form-control d-inline-block w-auto mx-2"
                                  onChange={(e) => {
                                    handleMassTaetigkeitsbereichChange(firmaId, e.target.value, selectedMitarbeiterInFirma);
                                  }}
                                >
                                  <option value="">Ausgewählten Mitarbeitern Tätigkeitsbereich zuweisen</option>
                                  {(taetigkeitsbereicheProFirma[firmaId] || []).map((bereich) => (
                                    <option key={bereich.id} value={bereich.id}>
                                      {bereich.bezeichnung} ({bereich.typ})
                                    </option>
                                  ))}
                                </Input>
                              </div>
                            )}
                              
                            
                            <div className="table-responsive">
                              <Table striped hover size="sm">
                                <thead>
                                  <tr>
                                    <th style={{ width: '40px' }}>
                                      <label className="custom-checkbox">
                                        <input 
                                          type="checkbox"
                                          className="custom-checkbox-input"
                                          checked={selectedMitarbeiterInFirma.length === firmaData.mitarbeiter.length && firmaData.mitarbeiter.length > 0}
                                          onChange={selectedMitarbeiterInFirma.length === firmaData.mitarbeiter.length ? 
                                            () => deselectAllMitarbeiterInFirma(firmaId) : 
                                            () => selectAllMitarbeiterInFirma(firmaId)}
                                        />
                                        <span className="custom-checkbox-box"></span>
                                      </label>
                                    </th>
                                    {previewData && <th style={{ width: '40px', textAlign: 'center' }} title="Import-Status">Status</th>}
                            {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'bereich' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                              <th key={csvColumn}>{fieldLabels[mapping[csvColumn]] || mapping[csvColumn] || csvColumn}</th>
                            ))}
                                    <th>Tätigkeitsbereich</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* CSV Mitarbeiter */}
                                  {displayMitarbeiter.map((mitarbeiter) => {
                                    const isPartialTaetigkeitsbereichMatch = Object.prototype.hasOwnProperty.call(partialTaetigkeitsbereichMatches, `${firmaId}-${mitarbeiter.index}`);
                                    const bereichColumn = Object.keys(mapping).find(key => mapping[key] === 'bereich') || '';
                                    
                                    const originalIndex = mitarbeiter.index;
                                    const isPartialFirmaMatch = Object.prototype.hasOwnProperty.call(partialMatches, originalIndex);
                                    const firmaColumn = Object.keys(mapping).find(key => mapping[key] === 'firma') || '';
                                    
                                    const employeeStatus = showPreview && previewData?.employeeStatus ? previewData.employeeStatus[mitarbeiter.index] : null;
                                    const statusIcons = {
                                      'create': { icon: '🟢', label: 'Neu anlegen', color: '#28a745' },
                                      'update': { icon: '🟡', label: 'Aktualisieren', color: '#ffc107' },
                                      'reactivate': { icon: '🔵', label: 'Reaktivieren', color: '#007bff' },
                                      'error': { icon: '❌', label: 'Fehler', color: '#dc3545' }
                                    };
                                    const status = statusIcons[employeeStatus];
                                    
                                    return (
                                      <tr key={mitarbeiter.index} style={isPartialFirmaMatch || isPartialTaetigkeitsbereichMatch ? { backgroundColor: '#fff3cd' } : {}}>
                                        <td>
                                          <label className="custom-checkbox">
                                            <input 
                                              type="checkbox"
                                              className="custom-checkbox-input"
                                              checked={selectedMitarbeiterInFirma.includes(mitarbeiter.index)}
                                              onChange={() => toggleMitarbeiterSelectionInFirma(firmaId, mitarbeiter.index)}
                                            />
                                            <span className="custom-checkbox-box"></span>
                                          </label>
                                        </td>
                                        {previewData && (
                                          <td style={{ textAlign: 'center', fontSize: '1.2em' }} title={status?.label || 'Unbekannt'}>
                                            {status ? (
                                              <span style={{ color: status.color, cursor: 'help' }}>{status.icon}</span>
                                            ) : (
                                              <span style={{ color: '#6c757d' }}>⏳</span>
                                            )}
                                          </td>
                                        )}
                                        {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'bereich' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                                          <td 
                                            key={csvColumn} 
                                            style={(isPartialTaetigkeitsbereichMatch && csvColumn === bereichColumn) || (isPartialFirmaMatch && csvColumn === firmaColumn) ? { backgroundColor: '#fff3cd' } : {}}
                                          >
                                            {mitarbeiter[csvColumn]}
                                          </td>
                                        ))}
                                        <td>
                                          <Input 
                                            type="select"
                                            className="form-control form-control-sm"
                                            value={taetigkeitsbereichAssignments[`${firmaId}-${mitarbeiter.index}`] || ''}
                                            onChange={(e) => handleTaetigkeitsbereichChange(firmaId, mitarbeiter.index, e.target.value)}
                                            disabled={employeeStatus === 'error'}
                                          >
                                            <option value="">Bitte auswählen</option>
                                            {(taetigkeitsbereicheProFirma[firmaId] || []).map((bereich) => (
                                              <option key={bereich.id} value={bereich.id}>
                                                {bereich.bezeichnung} ({bereich.typ})
                                              </option>
                                            ))}
                                          </Input>
                                        </td>
                                      </tr>
                                    );
                                  })}

                                  {/* Archiv-Kandidaten */}
                                  {displayArchives.map((emp) => (
                                    <tr key={`archive-${emp.id}`} className="table-secondary" style={{ opacity: 0.8 }}>
                                      <td></td>
                                      <td style={{ textAlign: 'center', fontSize: '1.2em' }} title="Wird archiviert">
                                        <span style={{ color: '#fd7e14' }}>🟠</span>
                                      </td>
                                      {/* Wir mappen hier die DB-Felder manuell, da sie nicht aus der CSV kommen */}
                                      {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'bereich' && mapping[csvColumn] !== 'firma').map((csvColumn) => {
                                        const dbField = mapping[csvColumn];
                                        let val = emp[dbField] || '-';
                                        if (dbField === 'geburtstag' && val !== '-') {
                                          val = new Date(val).toLocaleDateString('de-DE');
                                        }
                                        return <td key={csvColumn} className="text-muted italic"><i>{val}</i></td>;
                                      })}
                                      <td className="text-muted italic"><i>Wird archiviert (nicht in CSV)</i></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="p-3">Keine Mitarbeiter zum Anzeigen vorhanden.</p>
                    )
                  ) : !loading ? (
                    // Tätigkeitsbereich-Vorschau mit Gruppierung nach Firmen
                    (() => {
                      // Gruppiere die Daten nach Firmen basierend auf den Zuweisungen aus Schritt 2
                      const groupedTaetigkeitsbereiche = {};
                      csvData.forEach((row, index) => {
                        // Verwende die bereits zugeordnete Firma aus Schritt 2
                        const firmaId = firmaAssignments[index] || null;
                        const firma = firmen.find(f => f.id == firmaId) || { id: null, name: 'Nicht zugewiesen' };
                        
                        if (!groupedTaetigkeitsbereiche[firmaId]) {
                          groupedTaetigkeitsbereiche[firmaId] = {
                            firma: firma,
                            taetigkeitsbereiche: []
                          };
                        }
                        groupedTaetigkeitsbereiche[firmaId].taetigkeitsbereiche.push({ ...row, index });
                      });
                      
                      return Object.keys(groupedTaetigkeitsbereiche).length > 0 ? (
                        Object.entries(groupedTaetigkeitsbereiche).map(([firmaId, firmaData]) => (
                          <div key={firmaId || 'unassigned'} className="mb-4">
                            <h5 className="bg-light p-2 mb-0">{firmaData.firma.name || 'Nicht zugewiesen'}</h5>
                            <div className="table-responsive">
                              <Table striped>
                                <thead>
                                  <tr>
                                    {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                                      <th key={csvColumn}>{fieldLabels[mapping[csvColumn]] || mapping[csvColumn] || csvColumn}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {firmaData.taetigkeitsbereiche.map((taetigkeitsbereich) => (
                                    <tr key={taetigkeitsbereich.index}>
                                      {Object.keys(mapping).filter(csvColumn => mapping[csvColumn] !== '' && mapping[csvColumn] !== 'firma').map((csvColumn) => (
                                        <td key={csvColumn}>{taetigkeitsbereich[csvColumn]}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3">
                          <p>Keine Tätigkeitsbereiche zum Anzeigen vorhanden.</p>
                        </div>
                      );
                    })()
                  ) : null}
                </CardBody>
              </Card>
              
              {/* Erweiterte Felder: Meta-Spalten des CSV — nur manuell, default aus */}
              {entity === 'mitarbeiter' && metaCandidates.length > 0 && (
                <Card className="mb-4">
                  <CardHeader tag="h4">Erweiterte Felder (Metafelder)</CardHeader>
                  <CardBody>
                    <p className="mb-2">
                      Erkannte Meta-Spalten der CSV. Leere Zellen löschen den gespeicherten Wert.
                    </p>
                    {metaBlocking && (
                      <div className="alert alert-danger">
                        <strong>Zuordnungen auflösen:</strong> Ein Label trifft mehrere
                        Metafelder (bitte ordnen) oder mehrere eingeschlossene Spalten zeigen
                        auf dasselbe Metafeld (bitte eine Spalte abwählen).
                      </div>
                    )}
                    <div className="table-responsive">
                      <Table size="sm" striped>
                        <thead>
                          <tr>
                            <th style={{ width: '60px' }}>Übernehmen</th>
                            <th>CSV-Spalte</th>
                            <th>Metafeld</th>
                            <th>Herkunft</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metaCandidates.map((c) => (
                            <tr key={c.header} className={c.ambiguous && !metaAssigns[c.header] ? 'table-danger' : ''}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={metaInclude[c.header] === true}
                                  onChange={() => handleMetaInclude(c.header)}
                                />
                              </td>
                              <td>
                                {c.ambiguous && !metaAssigns[c.header]
                                  ? <strong className="text-danger">{c.header}</strong>
                                  : c.header}
                              </td>
                              <td>
                                {c.ambiguous ? (
                                  <Input
                                    type="select"
                                    className="form-control form-control-sm"
                                    value={metaAssigns[c.header] || ''}
                                    onChange={(e) => handleMetaAssign(c.header, e.target.value)}
                                  >
                                    <option value="">Bitte wählen (mehrdeutig)</option>
                                    {c.options.map((o) => (
                                      <option key={o.key} value={o.key}>
                                        {o.key} ({o.source})
                                      </option>
                                    ))}
                                  </Input>
                                ) : (
                                  <code>{c.key}</code>
                                )}
                              </td>
                              <td>{c.ambiguous && !metaAssigns[c.header] ? '—' : c.source}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}

              <div className="d-flex justify-content-between">
                <Button color="secondary" onClick={prevStep}>
                  Zurück
                </Button>
                <Button 
                  color="primary" 
                  size="lg" 
                  onClick={startImport}
                  disabled={loading || metaBlocking}
                >
                  Import starten
                </Button>
              </div>
            </div>
          )}
        </Col>
      </Row>
      {/* Import-Ergebnis anzeigen */}
      {importResult && (
        <Row className="mb-4">
          <Col>
            {importResult.error ? (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>Fehler beim Import:</strong> {importResult.error}
                <button type="button" className="btn-close" onClick={() => setImportResult(null)} aria-label="Close"></button>
              </div>
            ) : (
              <>
                {/* Detaillierte Informationen über übersprungene Datensätze */}
                {importResult.skippedRecords && importResult.skippedRecords.length > 0 && (
                  <div className="alert alert-warning alert-dismissible fade show" role="alert">
                    <strong>Übersprungene Datensätze:</strong>
                    <ul className="mb-0 mt-2">
                      {importResult.skippedRecords.map((record, index) => (
                        <li key={index}>
                          <strong>Grund:</strong> {record.reason}
                          {record.row && (
                            <div>
                              <strong>Daten:</strong> 
                              <ul className="mb-0">
                                {Object.entries(record.row)
                                  .filter(([key]) => key !== '__parsed_extra' && key !== 'index')
                                  .map(([key, value]) => (
                                    <li key={key}>{key}: {value}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    <button type="button" className="btn-close" onClick={() => setImportResult(null)} aria-label="Close"></button>
                  </div>
                )}
                {importResult.stats && importResult.stats.metaColumns && importResult.stats.metaColumns.length > 0 && (
                  <MetaColumnsTable
                    title="Metafelder (Import-Ergebnis)"
                    columns={importResult.stats.metaColumns}
                  />
                )}
              </>
            )}
          </Col>
        </Row>
      )}
  </>);
}

export default CSVImporter;
